import { initializeApp, type FirebaseApp } from 'firebase/app';
import { connectAuthEmulator, getAuth, type Auth } from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore, type Firestore } from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions, type Functions } from 'firebase/functions';
import { connectStorageEmulator, getStorage, type FirebaseStorage } from 'firebase/storage';

/**
 * apps/admin の Firebase 接続 (user app と同パターン)。
 * 旧 emulator 既知の落とし穴 (R-10): VITE_FIREBASE_PROJECT_ID は .firebaserc default と一致必須。
 *
 * 注意 (docs/110 Option A の撤回): クライアントの `authDomain` は **Google OAuth
 * (signInWithPopup) のハンドラ用ドメイン**であり、これを admin 独自ドメインに変えると
 * OAuth の redirect URI 不一致で Google サインインが壊れる。一方でパスワード再発行メールの
 * リンク先は **サーバ側の `notification.sendEmail.callbackUri`** (firebaseapp.com 固定、
 * `EMAIL_TEMPLATE_UPDATE_NOT_ALLOWED` でロック) で決まり、client の authDomain では変えられない。
 * よって authDomain は env (= `<project>.firebaseapp.com`) のまま据え置く。
 * 再発行メールのドメイン是正にはカスタム SMTP (docs/110 §5 Option B) が必要。
 */
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

export const app: FirebaseApp = initializeApp(firebaseConfig);
export const auth: Auth = getAuth(app);
export const db: Firestore = getFirestore(app);
export const storage: FirebaseStorage = getStorage(app);
// docs/03 §5 で region 統一 asia-northeast1
export const functions: Functions = getFunctions(app, 'asia-northeast1');

if (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true') {
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectStorageEmulator(storage, '127.0.0.1', 9199);
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
}
